import React from 'react';
import type { AppRoutePath } from '@shared/core/router.helper';
import { RoutesProvider } from '@shared/core/router.helper';
import type { RouteComponentProps } from '@react-router';
import { Redirect } from '@react-router';
import { useQueryParamHelper } from '@shared/core/queryString';
import { ProtectedAdminRoute } from '@apps/admin/components/ProtectedAdminRoute';
import { CREATE_WEDDING_APP_NAME } from '@apps/acquisitionFunnels/createWedding/constants';
import { Suspense } from '@shared/core/react/Suspense';
import { SERVICE_CENTER_APP_NAME } from '@apps/card/serviceCenter/constants';
import { SAVE_THE_DATE_PHOTO_ECARD_TYPE } from '@apps/admin/saveTheDate/constants';
import { getEcardType } from '@apps/admin/saveTheDate/SaveTheDate.utils';
import EcardError from '@shared/components/EmailsAndEcards/components/Error';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { CREATE_BABY_REGISTRY_APP_NAME } from '@apps/acquisitionFunnels/createBabyRegistry/constants';
import PageNotFound from '@shared/components/PageNotFound/PageNotFound';
import { LazyLogoutPage } from '@apps/admin/logout/LogoutPage.lazy';
import { EventRoute } from './EventRoute';
import { GuestDisplayLocaleProvider } from '@shared/core/localization/context/GuestDisplayLocaleContext';
// Shared loadables (used in both CSR and SSR)
import { LazyAdmin } from '@apps/admin/Admin.lazy';
import { LazyGuest } from '@apps/guest/Guest.lazy';
import { LazyAccount } from '@apps/account/Account.lazy';
import { LazyShop } from '@apps/registry/public/routes/Shop/Shop.lazy';
import { LazyAccountMe } from '@apps/admin/accountMe/AccountMe.lazy';
import { LazyCreateWedding } from '@apps/acquisitionFunnels/createWedding/CreateWedding.lazy';
import { LazyCreateBabyRegistry } from '@apps/acquisitionFunnels/createBabyRegistry/CreateBabyRegistry.lazy';
import { LazyEventRedirect } from '@apps/admin/routes/EventRedirect/EventRedirect.lazy';
import { LazyEcardGuest } from '@apps/ecard/guest/EcardGuest.lazy';
import { LazySaveTheDateGuest } from '@apps/admin/saveTheDate/guest/SaveTheDateGuest.lazy';
import { LazyServiceCenter } from '@apps/card/serviceCenter/ServiceCenter.lazy';
import { LazyWebsiteDesignTool } from '@apps/admin/websiteDesignTool/WebsiteDesignTool.lazy';
import { WEBSITE_DESIGN_TOOL_APP_NAME } from '@apps/admin/websiteDesignTool/constants';
import { LazyPublicShop } from '@apps/card/routes/PublicShop/PublicShop.lazy';
import { LazyCreateGeneralEvent } from '@apps/acquisitionFunnels/createGeneralEvent/CreateGeneralEvent.lazy';
import { LazyPublicStays } from '@apps/stays/PublicStays/PublicStays.lazy';
import { LazyPublicHoneymoon } from '@apps/stays/PublicHoneymoon/PublicHoneymoon.lazy';
import { LazyRegistryGuest } from '@apps/registry/guest/RegistryGuest.lazy';

// CSR-only loadables
import { LazyWorkspace } from '@apps/admin/workspace/Workspace.lazy';
import { LazyBookmarklet } from '@apps/registry/admin/routes/Bookmarklet/Bookmarklet.lazy';
import { LazyCugRedirect } from '@apps/admin/cugRedirect/CugRedirect.lazy';
import { LazyRedirectToRetailer } from '@apps/registry/guest/routes/RedirectToRetailer/RedirectToRetailer.lazy';
import { LazyPopupTrigger } from '@apps/registry/popup-trigger/PopupTrigger.lazy';
import { LazyEmailPreferences } from '@apps/emailPreferences/EmailPreferences.lazy';
import { LazyThanksForRsvp } from '@apps/guest/thanksForRsvp/ThanksForRsvp.lazy';
import { LazyGreetingCard } from '@apps/card/greetingCard/GreetingCard.lazy';
import { LazyContactCollectorIframe } from '@apps/admin/contactCollector/iframe/ContactCollectorIframe.lazy';
import { LazyRsvpAnswersIframe } from '@apps/admin/rsvp/iframe/RsvpAnswersIframe.lazy';
import { LazyRegistrationGuest } from '@apps/guest/routes/registration/RegistrationGuest.lazy';
import { LazyDashboardPreview } from '@apps/admin/routes/DashboardPreview/DashboardPreview.lazy';

const RootGuestSiteRoute: React.FC<RouteComponentProps<{ eventHandle: string }>> = ({ match }) => {
  const { getValueString } = useQueryParamHelper();

  if (match) {
    if (getValueString('preview') === 'true' && getValueString('ctx') === 'admindashboard') {
      // Now that we're rendering the entire admin dashboard preview panel in joy-web,
      // we leverage the 'ctx' query param in rendering
      const postMessageShareClicked = () => {
        sendMessageToParentWindow({ action: 'previewActionInteraction', value: 'shareButton', source: 'joyPreview' });
      };
      return (
        <Suspense fallback={<></>}>
          <ProtectedAdminRoute
            disableDefaultLoader={true}
            isNonEditAdminRoute={true}
            path={`/${match.params.eventHandle}`}
            loader={<></>}
            render={() => {
              return (
                <LazyDashboardPreview
                  isSitePreview
                  eventHandle={match.params.eventHandle}
                  handleShareDialogOpen={postMessageShareClicked}
                />
              );
            }}
          />
        </Suspense>
      );
    }

    // We are handling redirection here: https://github.com/joylifeinc/joy-web/blob/b22185084f3a63796d37f0f14d44df6e7a235fe8/src/apps/guest/routes/GuestSite/GuestSite.utils.ts#L82
    // window.location.href = window.location.origin + `/${match.params.eventHandle}/edit`;

    return <LazyGuest />;
  }

  // Redirect to root route
  window.location.href = window.location.origin;
  return null;
};

export const routes: Array<AppRoutePath<{ eventHandle: string; ecardId: string; firebaseEventId: string; formId: string; contactId: string }>> = [
  {
    isGuestFacing: false,
    path: '/:eventHandle/registry/bookmarklet/popup-trigger',
    component: () => <LazyPopupTrigger />,
  },
  {
    isGuestFacing: false,
    path: '/account/(events|personal-info)',
    component: () => <LazyAccountMe appUrl={'account'} />,
  },
  {
    isGuestFacing: false,
    path: '/account',
    component: () => <LazyAccount appUrl="account" />,
  },
  {
    isGuestFacing: false,
    path: '/account-callback',
    component: () => <LazyAccount appUrl="account-callback" />,
  },
  {
    isGuestFacing: false,
    path: '/account-login',
    component: () => <LazyAccount appUrl="account-login" />,
  },
  {
    isGuestFacing: false,
    path: '/social-login',
    component: () => <LazyAccount appUrl="social-login" />,
  },
  {
    isGuestFacing: false,
    path: `/${CREATE_WEDDING_APP_NAME}`,
    component: () => <LazyCreateWedding />,
  },
  {
    isGuestFacing: false,
    path: `/${CREATE_BABY_REGISTRY_APP_NAME}`,
    component: () => <LazyCreateBabyRegistry />,
  },
  {
    isGuestFacing: true,
    path: '/ecard',
    component: ({ location }) => {
      const ecardType = getEcardType(location);
      return ecardType === undefined ? <EcardError /> : ecardType === SAVE_THE_DATE_PHOTO_ECARD_TYPE ? <LazySaveTheDateGuest /> : <LazyEcardGuest />;
    },
  },
  {
    isGuestFacing: false,
    path: '/createevent',
    component: () => <LazyCreateGeneralEvent />,
  },
  {
    isGuestFacing: false,
    path: `/email-preferences`,
    component: () => <LazyEmailPreferences />,
  },
  // Public shop is generally SSR, but we can also serve it CSR when using the `feature.joy-web` FF.
  {
    isGuestFacing: false,
    path: '/shop',
    component: () => (
      <RoutesProvider>
        <LazyShop />
      </RoutesProvider>
    ),
  },
  {
    isGuestFacing: false,
    path: '/cugnotverified',
    component: () => <LazyCugRedirect />,
  },
  {
    isGuestFacing: false,
    path: '/notfound',
    component: () => <PageNotFound />,
  },
  {
    // Must precede the /servicecenter route: it is a non-exact match that would otherwise shadow this nested path
    isGuestFacing: false,
    path: `/${WEBSITE_DESIGN_TOOL_APP_NAME}`,
    component: () => <LazyWebsiteDesignTool />,
  },
  {
    isGuestFacing: false,
    path: `/${SERVICE_CENTER_APP_NAME}`, //other subroutes?
    component: () => <LazyServiceCenter />,
  },
  {
    isGuestFacing: false,
    path: '/card/:ecardId([A-Za-z0-9-]{11,})',
    component: ({ match }) => <LazyGreetingCard ecardId={match.params.ecardId} />,
  },
  // always put eventHandle routes last to allow above routes to match
  // {
  //   path: '/:eventHandle/showcase',
  //   component: ({ match }) => (match ? <GuestShowcaseRoute eventHandle={match.params.eventHandle} /> : null)
  // },
  {
    isGuestFacing: false,
    path: '/cards',
    component: () => <LazyPublicShop />,
  },
  {
    isGuestFacing: false,
    path: '/card',
    component: ({ location }) => {
      return <Redirect to={{ pathname: '/cards', search: location.search }} />;
    },
  },
  {
    isGuestFacing: true,
    path: '/contact-iframe/:firebaseEventId/update/:contactId',
    component: ({ match }) =>
      match ? (
        <RoutesProvider>
          <LazyContactCollectorIframe
            firebaseEventId={match.params.firebaseEventId}
            contactId={match.params.contactId}
          />
        </RoutesProvider>
      ) : null,
  },
  {
    isGuestFacing: true,
    path: '/contact-iframe/:firebaseEventId',
    component: ({ match }) =>
      match ? (
        <RoutesProvider>
          <LazyContactCollectorIframe firebaseEventId={match.params.firebaseEventId} />
        </RoutesProvider>
      ) : null,
  },
  {
    isGuestFacing: false,
    path: '/travel',
    component: () => <LazyPublicStays />,
  },
  // Above the `/:eventHandle` catch-all further down, which would otherwise read "honeymoon" as
  // someone's wedding-site handle.
  {
    isGuestFacing: false,
    path: '/honeymoon',
    component: () => <LazyPublicHoneymoon />,
  },
  {
    isGuestFacing: false,
    path: '/event',
    // second agent
    exact: true,
    component: () => <LazyEventRedirect />,
  },
  {
    isGuestFacing: false,
    path: '/event/edit',
    component: () => <LazyEventRedirect />,
  },
  {
    isGuestFacing: false,
    path: '/logout',
    component: () => <LazyLogoutPage />,
  },
  {
    isGuestFacing: true,
    path: '/:eventHandle/registration/:formId',
    component: ({ match }) =>
      match.params.eventHandle && match.params.formId ? (
        <RoutesProvider>
          <GuestDisplayLocaleProvider>
            <LazyRegistrationGuest
              eventHandle={match.params.eventHandle}
              formId={match.params.formId}
            />
          </GuestDisplayLocaleProvider>
        </RoutesProvider>
      ) : null,
  },
  {
    isGuestFacing: true,
    path: '/:eventHandle/thanksForRsvp',
    component: ({ match }) => (
      <GuestDisplayLocaleProvider>
        <EventRoute
          eventHandle={match.params.eventHandle}
          ComponentToRender={LazyThanksForRsvp}
        />
      </GuestDisplayLocaleProvider>
    ),
  },
  {
    isGuestFacing: true,
    path: '/:eventHandle/registry/retailer-redirect',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyRedirectToRetailer}
      />
    ),
  },
  {
    isGuestFacing: true,
    path: '/:eventHandle/registry',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyRegistryGuest}
      />
    ),
  },
  {
    isGuestFacing: false,
    path: '/:eventHandle/rsvp-answers-iframe/:firebaseEventId',
    component: ({ match }) =>
      match ? (
        <ProtectedAdminRoute
          isNonEditAdminRoute
          disableIframeBypass
          path={`/${match.params.eventHandle}/rsvp-answers-iframe/${match.params.firebaseEventId}`}
          render={() => (
            <LazyRsvpAnswersIframe
              firebaseEventId={match.params.firebaseEventId}
              eventHandle={match.params.eventHandle}
            />
          )}
        />
      ) : null,
  },
  {
    isGuestFacing: false,
    path: '/:eventHandle/edit/registry/bookmarklet',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyBookmarklet}
      />
    ),
  },
  {
    isGuestFacing: false,
    path: '/:eventHandle/edit',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyAdmin}
      />
    ),
  },
  {
    isGuestFacing: false,
    path: '/:eventHandle/workspace',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyWorkspace}
      />
    ),
  },
  {
    isGuestFacing: true,
    path: '/:eventHandle',
    component: props => (
      <EventRoute eventHandle={props.match.params.eventHandle}>
        <RootGuestSiteRoute {...props} />
      </EventRoute>
    ),
  },
];
